/**
 * DatGuiPlugin — dat.gui implementation of the GuiPlugin interface.
 * Migrated from PanelsElementGui.js.
 *
 * Store dependencies removed (6 operations):
 * - store.dispatch('setTransformControlsMode') → context.emit('command:setTransformMode')
 * - store.dispatch('PanelsScene/transformPanelsElement') → context.emit('command:transformElement')
 * - store.dispatch('PanelsScene/removePanelsElement') → context.emit('command:removeElement')
 * - store.state.PanelsEditor.navigationLocked → passed via update() call
 * - store.getters['transformControlsSpace'] → tracked from events
 * - PanelsDependenciesSingleton.getDatContainer() → context.container
 * - PanelsDependenciesSingleton.getTransformController() → context.emit('command:resizeGizmo')
 */

import * as dat from 'dat.gui';
import { SCENE_POSITION_LIMIT } from '../constants';
import type {
  GuiPlugin,
  GuiPluginContext,
  ElementGuiData,
  ElementGuiHandle,
} from './GuiPlugin';

interface DatController {
  name(name: string): DatController;
  onChange(fn: (value: unknown) => void): DatController;
  onFinishChange(fn: () => void): DatController;
  updateDisplay(): void;
}

class DatElementHandle implements ElementGuiHandle {
  private gui: dat.GUI | null = null;
  private numberControllers: DatController[] = [];
  private rotControllerX: DatController | null = null;
  private rotControllerY: DatController | null = null;
  private rotControllerZ: DatController | null = null;
  private dimensionsLabel: DatController | null = null;
  private guiTransformTimeout: ReturnType<typeof setTimeout> | null = null;

  private context: GuiPluginContext;
  private data: ElementGuiData;
  private position: { x: number; y: number; z: number };
  private rotation: { x: number; y: number; z: number };
  private rotationInDegrees: { x: number; y: number; z: number };
  // `scale` is the real (combined) mesh scale — what's shown, edited by the
  // gizmo, stored in Vuex, and persisted. For a panel it carries the image
  // aspect (e.g. [1, 0.5, 1]); for a model it's a plain [1,1,1] multiplier.
  private scale: { x: number; y: number; z: number };
  // The element's NATURAL aspect scale (longest side = 1 for panels; 1,1,1 for
  // models). "Reset scale" restores this so the panel keeps its aspect instead
  // of squashing to a square — it is NOT a hidden factor applied to `scale`.
  private scaleBase: { x: number; y: number; z: number };

  constructor(context: GuiPluginContext, data: ElementGuiData) {
    this.context = context;
    this.data = data;

    // Work with mutable copies
    this.position = { ...data.position };
    this.rotation = { ...data.rotation };
    this.rotationInDegrees = {
      x: data.rotation.x * (180 / Math.PI),
      y: data.rotation.y * (180 / Math.PI),
      z: data.rotation.z * (180 / Math.PI),
    };
    this.scale = { ...data.scale };
    // Natural aspect to reset to. Guard against 0/missing → 1.
    this.scaleBase = {
      x: data.width || 1,
      y: data.height || 1,
      z: 1,
    };

    this._setup();
  }

  private _setup(): void {
    this.gui = new dat.GUI({
      autoPlace: false,
      hideable: false,
      width: 310,
    });
    this.context.container.appendChild(this.gui.domElement);

    // Position controls
    this.gui.add(this, '_setTransformTranslate').name('Position');
    // Slider ranges match the scene clamp (±SCENE_POSITION_LIMIT) on every axis,
    // so the GUI and gizmo/drag share the same bounds.
    const L = SCENE_POSITION_LIMIT;
    const posX = this.gui
      .add(this.position, 'x', -L, L, 0.001)
      .onChange(() => this._emitTransformLive())
      .onFinishChange(() => this._handleTransformComplete()) as unknown as DatController;
    const posY = this.gui
      .add(this.position, 'y', -L, L, 0.001)
      .onChange(() => this._emitTransformLive())
      .onFinishChange(() => this._handleTransformComplete()) as unknown as DatController;
    const posZ = this.gui
      .add(this.position, 'z', -L, L, 0.001)
      .onChange(() => this._emitTransformLive())
      .onFinishChange(() => this._handleTransformComplete()) as unknown as DatController;
    this.gui.add(this, '_resetPosition').name('');

    // Rotation controls
    this.gui.add(this, '_setTransformRotate').name('Rotation');
    this.rotControllerX = this.gui
      .add(this.rotationInDegrees, 'x', -180, 180, 0.1)
      .onChange(() => { this._applyRotationInDegrees(); this._emitTransformLive(); })
      .onFinishChange(() => this._handleTransformComplete()) as unknown as DatController;
    this.rotControllerY = this.gui
      .add(this.rotationInDegrees, 'y', -180, 180, 0.1)
      .onChange(() => { this._applyRotationInDegrees(); this._emitTransformLive(); })
      .onFinishChange(() => this._handleTransformComplete()) as unknown as DatController;
    this.rotControllerZ = this.gui
      .add(this.rotationInDegrees, 'z', -180, 180, 0.1)
      .onChange(() => { this._applyRotationInDegrees(); this._emitTransformLive(); })
      .onFinishChange(() => this._handleTransformComplete()) as unknown as DatController;
    this.gui.add(this, '_resetRotation').name('');

    // Scale controls — independent x / y / z (like position & rotation).
    this.gui.add(this, '_setTransformScale').name('Scale');
    const onScaleChange = () => {
      this._emitResizeGizmo();
      this._updateDimensionsDisplay();
      this._emitTransformLive();
    };
    const scaleX = this.gui
      .add(this.scale, 'x', 0.1, 5, 0.001)
      .onChange(onScaleChange)
      .onFinishChange(() => this._handleTransformComplete()) as unknown as DatController;
    const scaleY = this.gui
      .add(this.scale, 'y', 0.1, 5, 0.001)
      .onChange(onScaleChange)
      .onFinishChange(() => this._handleTransformComplete()) as unknown as DatController;
    const scaleZ = this.gui
      .add(this.scale, 'z', 0.1, 5, 0.001)
      .onChange(onScaleChange)
      .onFinishChange(() => this._handleTransformComplete()) as unknown as DatController;
    this.gui.add(this, '_resetScale').name('');

    // Dimensions display
    this.dimensionsLabel = this.gui.add({ x: () => {} }, 'x').name('') as unknown as DatController;
    this._updateDimensionsDisplay();

    // Delete button
    this.gui.add(this, '_deleteElement').name('Delete');

    this.numberControllers = [posX, posY, posZ, scaleX, scaleY, scaleZ];
  }

  update(): void {
    if (this.gui) {
      this.refreshDisplay();
    }
  }

  refreshDisplay(): void {
    this.numberControllers.forEach((nc) => nc.updateDisplay());
    this._updateDimensionsDisplay();
    this._updateRotationInDegrees();
  }

  /**
   * Update internal transform data from external source (e.g. gizmo drag),
   * then refresh the dat.gui display. Rotation values are in radians.
   */
  updateTransform(data: {
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number };
    scale: { x: number; y: number; z: number };
  }): void {
    this.position.x = data.position.x;
    this.position.y = data.position.y;
    this.position.z = data.position.z;

    this.rotation.x = data.rotation.x;
    this.rotation.y = data.rotation.y;
    this.rotation.z = data.rotation.z;

    this.scale.x = data.scale.x;
    this.scale.y = data.scale.y;
    this.scale.z = data.scale.z;

    this.refreshDisplay();
  }

  destroy(): void {
    if (this.guiTransformTimeout) {
      clearTimeout(this.guiTransformTimeout);
      this.guiTransformTimeout = null;
    }
    if (this.gui) {
      this.gui.destroy();
      if (this.gui.domElement?.parentNode === this.context.container) {
        this.context.container.removeChild(this.gui.domElement);
      }
      this.gui = null;
    }
    this.numberControllers = [];
  }

  // --- Button handlers (used by dat.gui .add(this, 'methodName')) ---

  _setTransformTranslate = (): void => {
    this.context.emit('command:setTransformMode', 'translate');
  };
  _setTransformRotate = (): void => {
    this.context.emit('command:setTransformMode', 'rotate');
  };
  _setTransformScale = (): void => {
    this.context.emit('command:setTransformMode', 'scale');
  };

  _resetPosition = (): void => {
    this.position.x = 0;
    this.position.y = 0;
    this.position.z = 0;
    this._handleTransformComplete();
    this.numberControllers.forEach((nc) => nc.updateDisplay());
  };

  _resetRotation = (): void => {
    this.rotation.x = 0;
    this.rotation.y = 0;
    this.rotation.z = 0;
    this._updateRotationInDegrees();
    this._handleTransformComplete();
  };

  _resetScale = (): void => {
    // Restore the element's natural aspect (longest side = 1 for panels), NOT a
    // unit cube — so a 2:1 panel resets to [1, 0.5, 1] and keeps its aspect.
    this.scale.x = this.scaleBase.x;
    this.scale.y = this.scaleBase.y;
    this.scale.z = this.scaleBase.z;
    this._emitResizeGizmo();
    this._updateDimensionsDisplay();
    this.numberControllers.forEach((nc) => nc.updateDisplay());
    this._handleTransformComplete();
  };

  _deleteElement = (): void => {
    this.context.emit('command:removeElement', { sceneID: this.data.sceneID });
  };

  // --- Internal helpers ---

  private _emitResizeGizmo(): void {
    this.context.emit('command:resizeGizmo', undefined);
  }

  /**
   * Emit transform for real-time feedback during slider drag.
   * Uses a separate 'live' command so the wrapper can update the engine directly
   * without a Vuex round-trip (matching the old system's direct Vector3 mutation).
   */
  private _emitTransformLive(): void {
    this.context.emit('command:transformElementLive', {
      sceneID: this.data.sceneID,
      position: [this.position.x, this.position.y, this.position.z],
      rotation: [
        this.rotationInDegrees.x,
        this.rotationInDegrees.y,
        this.rotationInDegrees.z,
      ],
      scale: [this.scale.x, this.scale.y, this.scale.z],
    });
  }

  private _applyRotationInDegrees(): void {
    // Force world space when rotating via GUI
    this.context.emit('command:setTransformSpace', 'world');
    this._emitResizeGizmo();

    const DEG2RAD = Math.PI / 180;
    this.rotation.x = this.rotationInDegrees.x * DEG2RAD;
    this.rotation.y = this.rotationInDegrees.y * DEG2RAD;
    this.rotation.z = this.rotationInDegrees.z * DEG2RAD;
  }

  private _updateRotationInDegrees(): void {
    const RAD2DEG = 180 / Math.PI;
    this.rotationInDegrees.x = this.rotation.x * RAD2DEG;
    this.rotationInDegrees.y = this.rotation.y * RAD2DEG;
    this.rotationInDegrees.z = this.rotation.z * RAD2DEG;
    this.rotControllerX?.updateDisplay();
    this.rotControllerY?.updateDisplay();
    this.rotControllerZ?.updateDisplay();
  }

  private _updateDimensionsDisplay(): void {
    if (this.dimensionsLabel) {
      this.dimensionsLabel.name(`${this.scale.x.toFixed(2)}m x ${this.scale.y.toFixed(2)}m`);
    }
  }

  private _handleTransformComplete(): void {
    if (this.guiTransformTimeout) {
      clearTimeout(this.guiTransformTimeout);
      this.guiTransformTimeout = null;
    }
    this.guiTransformTimeout = setTimeout(() => {
      this.guiTransformTimeout = null;
      this.context.emit('command:transformElement', {
        sceneID: this.data.sceneID,
        position: [this.position.x, this.position.y, this.position.z],
        rotation: [
          this.rotationInDegrees.x,
          this.rotationInDegrees.y,
          this.rotationInDegrees.z,
        ],
        scale: [this.scale.x, this.scale.y, this.scale.z],
      });
    }, 100);
  }
}

/**
 * DatGuiPlugin — dat.gui-based transform GUI for the editor.
 */
export class DatGuiPlugin implements GuiPlugin {
  private context: GuiPluginContext | null = null;
  private activeHandle: DatElementHandle | null = null;

  init(context: GuiPluginContext): void {
    this.context = context;
  }

  createElementGui(element: ElementGuiData): ElementGuiHandle {
    if (!this.context) {
      throw new Error('DatGuiPlugin: init() must be called before createElementGui()');
    }
    // Destroy any existing handle
    if (this.activeHandle) {
      this.activeHandle.destroy();
    }
    this.activeHandle = new DatElementHandle(this.context, element);
    return this.activeHandle;
  }

  dispose(): void {
    if (this.activeHandle) {
      this.activeHandle.destroy();
      this.activeHandle = null;
    }
    this.context = null;
  }
}

export default DatGuiPlugin;
